import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjectStore } from '@/store/projectStore.ts';
import { useAudioStore } from '@/store/audioStore.ts';
import { exportNLE, estimateZipSizeMB, NLE_SIZE_GUARD_THRESHOLD_MB, type ExportNLEPhase } from './nleExport.ts';
import { buildNLEExport, type AudioBytePeeker } from './nleManifestBuilder.ts';
import { peekAudioBytes, getEmbeddedDatabase } from '@/previs/sqlReader.ts';
import { getBlob } from '@/previs/audioBlobStore.ts';
import { capabilities } from '@/capabilities.ts';
import { showToast } from '@/components/Toast.tsx';
import styles from './NLEExportTab.module.css';

type Mode = 'idle' | 'running' | 'error' | 'sizeGuard';

const FPS_OPTIONS = [
  { value: 24,     ntsc: false, label: '24 fps' },
  { value: 23.976, ntsc: true,  label: '23.976 fps' },
  { value: 25,     ntsc: false, label: '25 fps' },
  { value: 29.97,  ntsc: true,  label: '29.97 fps' },
  { value: 30,     ntsc: false, label: '30 fps' },
];

/** Returns the FPS_OPTIONS index whose integer value matches the project rate.
 *  Falls back to 24 fps (cinema default) when there's no match. */
function defaultFpsIndex(projectFps: number | undefined): number {
  if (projectFps == null) return 0;
  const i = FPS_OPTIONS.findIndex((opt) => Math.round(opt.value) === Math.round(projectFps) && !opt.ntsc);
  return i >= 0 ? i : 0;
}

const ASPECT_TO_RES: Record<string, { width: number; height: number }> = {
  '16:9':   { width: 1920, height: 1080 },
  '9:16':   { width: 1080, height: 1920 },
  '2.39:1': { width: 2048, height: 858 },
  '1.85:1': { width: 1998, height: 1080 },
  '4:3':    { width: 1440, height: 1080 },
  '1:1':    { width: 1080, height: 1080 },
};

interface Props {
  onClose: () => void;
}

export function NLEExportTab({ onClose }: Props) {
  const { t } = useTranslation('export');
  const project = useProjectStore((s) => s.project);
  const audioClips = useAudioStore((s) => s.audioClips);

  const projectFps = project?.frameRate;
  const projectFpsIndex = defaultFpsIndex(projectFps);
  const [fpsIndex, setFpsIndex] = useState(projectFpsIndex);
  const [mode, setMode] = useState<Mode>('idle');
  const [phase, setPhase] = useState<ExportNLEPhase>('planning');
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [errorMsg, setErrorMsg] = useState('');
  const [estimateMB, setEstimateMB] = useState<number | null>(null);

  // Use a ref for the cancellation flag so closures always read the latest value.
  // State (isCancelledDisplay) drives the disabled state of the Cancel button.
  const cancelRef = useRef(false);
  const [isCancelledDisplay, setIsCancelledDisplay] = useState(false);

  const fpsChoice = FPS_OPTIONS[fpsIndex];
  const aspect = project?.aspectRatio ?? '16:9';
  const { width, height } = ASPECT_TO_RES[aspect] ?? ASPECT_TO_RES['16:9'];

  const sceneCount = project?.scenes.length ?? 0;
  const shotCount = useMemo(
    () => project?.scenes.reduce((n, s) => n + s.shots.length, 0) ?? 0,
    [project],
  );
  const audioCount = audioClips.length;

  // Size estimate — recompute when project, audioClips, fps, or dimensions change.
  useEffect(() => {
    if (!project) return;
    let cancelled = false;
    (async () => {
      // Embedded mode: open one DB for the entire estimate pass and close in finally.
      // Standalone: peeker reads from the in-memory blob store (no DB).
      let db: Awaited<ReturnType<typeof getEmbeddedDatabase>> | null = null;
      try {
        if (capabilities.bridge) {
          db = await getEmbeddedDatabase();
        }
        const peeker: AudioBytePeeker = capabilities.bridge
          ? async (clipId, n) => peekAudioBytes(db!, clipId, n)
          : async (clipId, n) => {
              const bytes = getBlob(clipId);
              return bytes ? bytes.slice(0, n) : null;
            };
        const { manifest } = await buildNLEExport(
          project, audioClips,
          { fps: fpsChoice.value, ntsc: fpsChoice.ntsc, width, height },
          peeker,
        );
        if (!cancelled) setEstimateMB(estimateZipSizeMB(manifest));
      } catch (err) {
        if (!cancelled) {
          console.warn('[NLE Export] Estimate failed:', err);
          setEstimateMB(null);
        }
      } finally {
        if (db) db.close();
      }
    })();
    return () => { cancelled = true; };
  }, [project, audioClips, fpsChoice, width, height]);

  function startExport(): void {
    if (!project) return;
    if (!capabilities.bridge && estimateMB != null && estimateMB > NLE_SIZE_GUARD_THRESHOLD_MB) {
      setMode('sizeGuard');
      return;
    }
    void runExport();
  }

  async function runExport(): Promise<void> {
    if (!project) return;
    cancelRef.current = false;
    setIsCancelledDisplay(false);
    setMode('running');
    setProgress({ current: 0, total: 0 });
    setPhase('planning');

    try {
      await exportNLE(project, audioClips, {
        fps: fpsChoice.value, ntsc: fpsChoice.ntsc, width, height,
      }, {
        onProgress: (p, c, total) => { setPhase(p); setProgress({ current: c, total }); },
        isCancelled: () => cancelRef.current,
      });
      if (cancelRef.current) {
        showToast(t('nleCancelledToast'));
      } else {
        showToast(t('nleSuccessToast', { filename: `${project.title}_NLE.zip` }));
      }
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (message === 'cancelled' || cancelRef.current) {
        showToast(t('nleCancelledToast'));
        onClose();
      } else {
        setErrorMsg(message);
        setMode('error');
      }
    }
  }

  if (mode === 'sizeGuard') {
    return (
      <div className={styles.body}>
        <h3 className={styles.heading}>{t('nleSizeGuardTitle')}</h3>
        <p className={styles.bodyText}>{t('nleSizeGuardBody', { size: `${Math.round(estimateMB ?? 0)} MB` })}</p>
        <div className={styles.actions}>
          <button className={styles.cancelBtn} onClick={() => setMode('idle')}>{t('cancelButton')}</button>
          <button className={styles.exportBtn} onClick={() => { setMode('idle'); void runExport(); }}>{t('continueButton')}</button>
        </div>
      </div>
    );
  }

  if (mode === 'running') {
    const labelKey = `nleProgress${phase[0].toUpperCase() + phase.slice(1)}` as const;
    return (
      <div className={styles.body}>
        <h3 className={styles.heading}>{t('nleRunningTitle')}</h3>
        <p className={styles.bodyText}>{t(labelKey, { current: progress.current, total: progress.total })}</p>
        <div className={styles.bar}>
          <div
            className={styles.barFill}
            style={{ width: progress.total > 0 ? `${(progress.current / progress.total) * 100}%` : '0%' }}
          />
        </div>
        <div className={styles.actions}>
          <button
            className={styles.cancelBtn}
            onClick={() => { cancelRef.current = true; setIsCancelledDisplay(true); }}
            disabled={isCancelledDisplay}
          >
            {t('cancelButton')}
          </button>
        </div>
      </div>
    );
  }

  if (mode === 'error') {
    return (
      <div className={styles.body}>
        <h3 className={styles.heading}>{t('nleErrorTitle')}</h3>
        <p className={styles.errorText}>{errorMsg}</p>
        <div className={styles.actions}>
          <button className={styles.cancelBtn} onClick={() => { setErrorMsg(''); setMode('idle'); }}>{t('okButton')}</button>
        </div>
      </div>
    );
  }

  // idle
  return (
    <div className={styles.body}>
      <label className={styles.row}>
        <span>{t('nleFpsLabel')}</span>
        <select
          className={styles.select}
          value={fpsIndex}
          onChange={(e) => setFpsIndex(parseInt(e.target.value, 10))}
        >
          {FPS_OPTIONS.map((opt, i) => (
            <option key={opt.label} value={i}>
              {i === projectFpsIndex && projectFps != null ? `${opt.label} ★` : opt.label}
            </option>
          ))}
        </select>
      </label>
      {projectFps != null && (
        <div className={styles.fpsHint}>
          {t('nleFpsProjectHint', { fps: projectFps })}
        </div>
      )}

      <div className={styles.summary}>
        <div>{width}×{height}</div>
        <div>{t('nleProjectStats', { sceneCount, shotCount, audioCount })}</div>
        {estimateMB != null && <div>{t('nleEstimatedSize', { size: `${Math.round(estimateMB)} MB` })}</div>}
      </div>

      <div className={styles.actions}>
        <button className={styles.cancelBtn} onClick={onClose}>{t('cancelButton')}</button>
        <button
          className={styles.exportBtn}
          onClick={startExport}
          disabled={!project}
        >
          {t('nleExportButton')}
        </button>
      </div>
    </div>
  );
}
